import * as React from 'react';

/**
 * InputSelection represents a selection in the input
 * Here are following possible selection positions (marked with ':' symbol):
 *
 * For text 'abcdef' –  : a : b : c : d : e : f :
 *         positions – (0) (1) (2) (3) (4) (5) (6)
 *
 */
type InputSelection = {
  end: number;
  start: number;
  direction: 'forward' | 'backward' | 'none';
};

export const useInputSelection = (
  inputRef: React.RefObject<HTMLInputElement>,
) => {
  const [selection, setSelection] = React.useState<InputSelection | null>(null);

  const onBlur = React.useCallback(() => {
    setSelection(null);
  }, []);

  // @ts-expect-error Type fails in React 18
  const onFocus = React.useCallback((event) => {
    const target = event.target;
    const HTMLInputElementConstructor =
      target?.ownerDocument?.defaultView?.HTMLInputElement;

    if (
      !HTMLInputElementConstructor ||
      !(target instanceof HTMLInputElementConstructor)
    ) {
      return;
    }

    const value = target.value;
    const maxLength = target.maxLength;

    const start = Math.min(value.length, maxLength - 1);
    const end = start;
    const direction = start < end ? 'forward' : 'none';

    if (target.selectionStart !== start || target.selectionEnd !== end) {
      requestAnimationFrame(() => {
        target.setSelectionRange(start, end, direction);
      });
    }

    setSelection({end, start, direction});
  }, []);

  const onSelectionChange = React.useCallback((event: Event) => {
    const input = event.target as HTMLInputElement | null;
    const HTMLInputElementConstructor =
      input?.ownerDocument?.defaultView?.HTMLInputElement;

    if (
      !input ||
      (HTMLInputElementConstructor &&
        !(input instanceof HTMLInputElementConstructor)) ||
      input.ownerDocument?.activeElement !== input
    ) {
      return;
    }

    let end: number | null = null;
    let start: number | null = null;
    let direction: InputSelection['direction'] | undefined = undefined;

    const {value, maxLength, selectionEnd, selectionStart, selectionDirection} =
      input;

    const shouldValidate =
      !!value && selectionEnd !== null && selectionStart !== null;

    if (shouldValidate) {
      if (selectionStart === 0) {
        /**
         * If the selection is at the beginning of the input, we need to set
         * the selection to select the next character.
         */
        start = 0;
        end = 0;
        direction = 'forward';
      } else if (selectionStart > maxLength - 1) {
        /**
         * If the selection is at the end of the input, we need to set the
         * selection to the previous character.
         */
        start = maxLength - 1;
        end = start;
        direction = 'backward';
      } else if (value.length > 1) {
        /**
         * Restrict selection only to one symbol
         */
        start = Math.min(selectionStart, maxLength - 1);
        end = start;
      }

      if (start !== null && end !== null && start !== end) {
        input.setSelectionRange(start, end, direction);
      }
    }

    setSelection({
      end: end || selectionEnd || 0,
      start: start || selectionStart || 0,
      direction: direction || selectionDirection || 'none',
    });
  }, []);

  React.useLayoutEffect(() => {
    const input = inputRef.current;

    input?.addEventListener('blur', onBlur);
    input?.addEventListener('focus', onFocus);
    input?.addEventListener('selectionchange', onSelectionChange);

    return () => {
      input?.removeEventListener('blur', onBlur);
      input?.removeEventListener('focus', onFocus);
      input?.removeEventListener('selectionchange', onSelectionChange);
    };
  }, [onBlur, onFocus, inputRef, onSelectionChange]);

  return selection;
};
